import { useState, useCallback, Suspense, useMemo } from 'react'
import { Canvas } from '@react-three/fiber'
import { Preload, ScrollControls, Scroll, useScroll } from '@react-three/drei'
import Galaxy from './components/Galaxy'
import ParticleText from './components/ParticleText'
import Effects from './components/Effects'

// Detect mobile for responsive adjustments
const isMobile = typeof window !== 'undefined' && window.innerWidth < 768

// Scroll button component
function ScrollButton({ targetPage, children, className = '' }) {
    const scroll = useScroll()

    const handleClick = (e) => {
        e.preventDefault()
        scroll.el.scrollTo({
            top: scroll.el.scrollHeight * (targetPage / 6),
            behavior: 'smooth'
        })
    }

    return (
        <button onClick={handleClick} className={`cta-button ${className}`}>
            {children}
        </button>
    )
}

function ScrollContent({ mouse }) {
    return (
        <>
            {/* 3D Scene - ParticleText only on desktop */}
            <Galaxy mouse={mouse} />
            {!isMobile && <ParticleText text="SIMPLE DOTS" position={[0, 2.5, 4]} />}
            <Effects />

            {/* HTML Content */}
            <Scroll html>
                {/* Hero Section */}
                <section className="scroll-section hero-section">
                    {/* Mobile logo - only visible on mobile */}
                    {isMobile && (
                        <div className="mobile-logo-container">
                            <img src="/simple-dots-logo.png" alt="Simple Dots" className="mobile-logo" />
                        </div>
                    )}
                    <div className="hero-content">
                        <h1 className="hero-headline">
                            See your customer journey <span>like never before</span>
                        </h1>
                        <p className="hero-sub">
                            Every touchpoint becomes a star. Every deal, a constellation.
                            Transform your HubSpot data into a living galaxy.
                        </p>
                        <ScrollButton targetPage={1} className="learn-more">
                            Learn More
                        </ScrollButton>
                    </div>
                </section>

                {/* Features Section */}
                <section className="scroll-section features-section">
                    <div className="section-header">
                        <h2>Visualize What Matters</h2>
                        <p className="section-intro">The galaxy metaphor makes complex customer journeys instantly understandable</p>
                    </div>

                    <div className="features-grid">
                        <div className="feature-card">
                            <h3>Stellar Evolution</h3>
                            <p>Colors shift from cool blue (MQA) to hot amber (Opportunity) to green supernova (Won). See deal heat at a glance.</p>
                        </div>
                        <div className="feature-card">
                            <h3>Time Decay</h3>
                            <p>Recent events glow bright, old ones fade. Instantly spot stalled deals and cold prospects before they go dark.</p>
                        </div>
                        <div className="feature-card">
                            <h3>Cluster Analysis</h3>
                            <p>Hover over any region to reveal patterns. AI-generated insights explain what's happening in each cluster.</p>
                        </div>
                        <div className="feature-card">
                            <h3>Real-Time KPIs</h3>
                            <p>Pipeline metrics update as you zoom, filter, and explore. Context-aware numbers, always in sync.</p>
                        </div>
                        <div className="feature-card">
                            <h3>Journey Tracking</h3>
                            <p>Super Mario Bros 3-style progression. Track restarts, high scores, and the full path to conversion.</p>
                        </div>
                        <div className="feature-card">
                            <h3>WebGPU Power</h3>
                            <p>Hardware-accelerated rendering handles 10,000+ events smoothly. Built for enterprise scale.</p>
                        </div>
                    </div>
                </section>

                {/* Demo Section */}
                <section className="scroll-section demo-section">
                    <div className="section-header">
                        <h2>From Spreadsheet to Galaxy</h2>
                        <p className="section-intro">Your HubSpot data, transformed into spatial intelligence</p>
                    </div>
                    <div className="demo-placeholder">
                        <div className="play-button">▶</div>
                    </div>
                </section>

                {/* Use Cases Section */}
                <section className="scroll-section usecases-section">
                    <div className="section-header">
                        <h2>Built for Revenue Teams</h2>
                        <p className="section-intro">Different views for different roles</p>
                    </div>

                    <div className="usecases-grid">
                        <div className="usecase-card">
                            <h3>Sales Leaders</h3>
                            <h4>Pipeline Health at a Glance</h4>
                            <p>See which opportunities are heating up, which are cooling down, and predict risk before deals go cold.</p>
                        </div>
                        <div className="usecase-card">
                            <h3>Account Executives</h3>
                            <h4>Territory Intelligence</h4>
                            <p>Filter by product, campaign, or rep to see performance visually. Spot champion changes across your book of business.</p>
                        </div>
                        <div className="usecase-card">
                            <h3>Marketing Ops</h3>
                            <h4>Attribution That Makes Sense</h4>
                            <p>Watch which campaigns produce the brightest stars. See the full journey from first touch to close.</p>
                        </div>
                    </div>
                </section>

                {/* Tech Stack */}
                <section className="scroll-section tech-section">
                    <div className="tech-grid">
                        <div className="tech-item">
                            <span className="tech-label">Rendering</span>
                            <span className="tech-value">WebGPU + Canvas2D</span>
                        </div>
                        <div className="tech-item">
                            <span className="tech-label">Data Source</span>
                            <span className="tech-value">HubSpot / JSON</span>
                        </div>
                        <div className="tech-item">
                            <span className="tech-label">Query</span>
                            <span className="tech-value">Natural Language</span>
                        </div>
                        <div className="tech-item">
                            <span className="tech-label">Deployment</span>
                            <span className="tech-value">Electron / Web</span>
                        </div>
                    </div>
                </section>

                {/* Coming Soon Section */}
                <section className="scroll-section cta-section">
                    <div className="section-card coming-soon-card">
                        <h2 className="coming-soon">Coming Soon</h2>
                        <p className="hubspot-tagline">to a HubSpot near you</p>
                    </div>
                </section>
            </Scroll>
        </>
    )
}

export default function App() {
    const [mouse, setMouse] = useState({ x: 0, y: 0 })

    const handleMouseMove = useCallback((e) => {
        setMouse({
            x: (e.clientX / window.innerWidth) * 2 - 1,
            y: -(e.clientY / window.innerHeight) * 2 + 1
        })
    }, [])

    return (
        <div onMouseMove={handleMouseMove} className="app-container">
            <Canvas
                camera={{ position: isMobile ? [0, 2, 14] : [0, 2, 12], fov: isMobile ? 65 : 60 }}
                gl={{ antialias: !isMobile, alpha: true }}
                dpr={[1, isMobile ? 1.5 : 2]}
            >
                <color attach="background" args={['#050810']} />
                <fog attach="fog" args={['#050810', 15, 35]} />

                <ScrollControls pages={6} damping={0.25}>
                    <Suspense fallback={null}>
                        <ScrollContent mouse={mouse} />
                    </Suspense>
                </ScrollControls>

                <Preload all />
            </Canvas>
        </div>
    )
}
